import React, { useState } from 'react';
import { usePDF } from '../context/PDFContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { startPaystackCheckout } from '../lib/paystackCheckout';
import { Lock, Zap, FileDown, LogIn, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const LockOverlay = () => {
  const { isLocked, lockStatus, refreshLock } = usePDF();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  if (!isLocked) return null;

  const buy = async (planId) => {
    setLoadingPlan(planId);
    try {
      const result = await startPaystackCheckout(planId, {
        onPaid: async () => {
          await refreshLock();
          refreshUser();
        },
      });
      if (result === 'paid') {
        toast.success('Payment confirmed — editing unlocked!');
      } else if (result === 'failed') {
        toast.error('Payment was not completed. Please try again.');
      }
    } catch (e) {
      toast.error(e.message || 'Something went wrong');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div
      className="absolute inset-0 z-40 backdrop-blur-md bg-background/70 flex items-center justify-center p-4"
      data-testid="editor-lock-overlay"
    >
      <div className="bg-card border border-border rounded-xl max-w-sm w-full p-6 shadow-2xl text-center">
        <div className="w-14 h-14 bg-amber-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-amber-500" />
        </div>
        {!lockStatus.signedIn ? (
          <>
            <h3 className="text-lg font-semibold mb-2">Sign in to start editing</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Sign in to use the editing tools. Your first document download each day is free.
            </p>
            <Button className="w-full" onClick={() => navigate('/login')} data-testid="lock-signin-btn">
              <LogIn className="w-4 h-4 mr-2" /> Sign in
            </Button>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold mb-2">Unlock Editing</h3>
            <p className="text-sm text-muted-foreground mb-6">
              You've used your free document today. Unlock this document, or go unlimited —
              payment confirms right here, your PDF stays loaded.
            </p>
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={() => buy('subscription')}
                disabled={loadingPlan !== null}
                data-testid="lock-subscribe-btn"
              >
                {loadingPlan === 'subscription' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                {loadingPlan === 'subscription' ? 'Waiting for payment...' : 'Get Unlimited Access — R49/mo'}
              </Button>
              <p className="text-[11px] text-muted-foreground">R1 card check · 3-day trial · cancel anytime</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => buy('single')}
                disabled={loadingPlan !== null}
                data-testid="lock-single-btn"
              >
                {loadingPlan === 'single' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                {loadingPlan === 'single' ? 'Waiting for payment...' : 'Unlock this document — R8'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LockOverlay;
